import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import {
  ArrowDownLeft,
  ArrowRight,
  ArrowRightLeft,
  ArrowUpRight,
  BarChart3,
  Monitor,
  RefreshCw,
  Trophy,
  WalletCards,
} from "lucide-react";

import Navbar from "../../components/Navbar";
import Sidebar from "../../components/Sidebar";
import Loader from "../../components/Loader";

import authService from "../../services/authService";
import settingsService from "../../services/settingsService";
import shopService from "../../services/shopService";

const AnimatedCounter = ({ value, duration = 1200 }) => {
  const [count, setCount] = useState(0);
  const startTimeRef = useRef(null);

  useEffect(() => {
    startTimeRef.current = null;
    const target = Number(value || 0);

    const animate = (timestamp) => {
      if (!startTimeRef.current) startTimeRef.current = timestamp;
      const progress = Math.min(
        (timestamp - startTimeRef.current) / duration,
        1
      );
      const eased = 1 - Math.pow(1 - progress, 3);

      setCount(Math.floor(eased * target));

      if (progress < 1) {
        requestAnimationFrame(animate);
      }
    };

    const frame = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(frame);
  }, [value, duration]);

  return <>{count.toLocaleString("en-IN")}</>;
};

const ShopDashboard = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [user, setUser] = useState(null);
  const [dashData, setDashData] = useState({});
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  // Coins Quick Action Modal State (Direct Transfer / Take)
  const [coinsModal, setCoinsModal] = useState(null); // null | { mode: 'transfer' | 'take' }
  const [computersList, setComputersList] = useState([]);
  const [selectedComputerId, setSelectedComputerId] = useState("");
  const [coinAmount, setCoinAmount] = useState("");
  const [loadingComputers, setLoadingComputers] = useState(false);
  const [submittingCoins, setSubmittingCoins] = useState(false);

  const formatCoins = (value) =>
    Number(value || 0).toLocaleString("en-IN", {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    });

  const fetchShopData = useCallback(async ({ silent = false } = {}) => {
    try {
      if (silent) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }

      const response = await settingsService.getDashboard();

      if (response?.success) {
        setDashData(response.dashboard || {});
      } else if (!silent) {
        toast.error(response?.message || "Failed to load Shop dashboard.");
      }
    } catch (error) {
      if (!silent) {
        toast.error(
          error?.response?.data?.message || "Failed to load Shop dashboard."
        );
      }
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    setUser(authService.getCurrentUser());
    fetchShopData();
  }, [fetchShopData]);

  useEffect(() => {
    const interval = window.setInterval(() => {
      fetchShopData({ silent: true });
    }, 5000);

    return () => window.clearInterval(interval);
  }, [fetchShopData]);

  const wallet = Number(dashData?.wallet || 0);
  const totalComputers = Number(dashData?.total_computers || dashData?.total_players || 0);
  const pendingWinners = Number(dashData?.pending_winners || 0);

  const transferredCoins = Number(
    dashData?.total_transfer_coins ??
    dashData?.transfer_coins ??
    dashData?.total_transferred ??
    dashData?.coins_transferred ??
    dashData?.total_coins_transferred ??
    dashData?.transferred_coins ??
    0
  );

  const takenCoins = Number(
    dashData?.total_take_coins ??
    dashData?.take_coins ??
    dashData?.total_taken ??
    dashData?.coins_taken ??
    dashData?.total_coins_taken ??
    dashData?.taken_coins ??
    0
  );

  const openCoinsModal = async (mode) => {
    setCoinsModal({ mode });
    setCoinAmount("");
    setSelectedComputerId("");
    setLoadingComputers(true);
    try {
      const response = await shopService.getComputers();
      const list = Array.isArray(response?.computers) ? response.computers : [];
      setComputersList(list);
      if (list.length > 0) {
        setSelectedComputerId(String(list[0].id));
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to fetch computers list.");
    } finally {
      setLoadingComputers(false);
    }
  };

  const closeCoinsModal = () => {
    setCoinsModal(null);
    setCoinAmount("");
    setSelectedComputerId("");
  };

  const handleCoinsSubmit = async (e) => {
    e.preventDefault();
    if (!selectedComputerId) {
      toast.error("Please select a computer.");
      return;
    }
    const parsedAmount = Number(coinAmount);
    if (!parsedAmount || parsedAmount <= 0) {
      toast.error("Please enter a valid coin amount greater than 0.");
      return;
    }

    const mode = coinsModal?.mode;

    const selectedComp = computersList.find(
      (c) => String(c.id) === String(selectedComputerId)
    );
    const selectedCompWallet = selectedComp
      ? (selectedComp.wallet ?? selectedComp.coins ?? selectedComp.current_balance ?? selectedComp.balance ?? selectedComp.credits ?? 0)
      : 0;

    if (mode === "take" && selectedCompWallet > 0 && parsedAmount > selectedCompWallet) {
      toast.error(`Cannot take more coins than PC wallet (Available: 🪙 ${formatCoins(selectedCompWallet)})`);
      return;
    }

    try {
      setSubmittingCoins(true);
      let res;
      if (mode === "take") {
        res = await shopService.takeFromComputer(
          selectedComputerId,
          parsedAmount
        );
      } else {
        res = await shopService.transferToComputer(
          selectedComputerId,
          parsedAmount
        );
      }

      toast.success(
        res?.message ||
          (mode === "take"
            ? "Coins taken from computer successfully!"
            : "Coins transferred to computer successfully!")
      );

      // Instant local state update for real-time PC wallet UI refresh
      setComputersList((prev) =>
        prev.map((c) => {
          if (String(c.id) === String(selectedComputerId)) {
            const oldBal = Number(c.current_balance ?? c.wallet ?? c.coins ?? c.balance ?? 0);
            const newBal = mode === "take" ? Math.max(0, oldBal - parsedAmount) : oldBal + parsedAmount;
            return {
              ...c,
              current_balance: newBal,
              wallet: newBal,
              coins: newBal,
              balance: newBal,
            };
          }
          return c;
        })
      );

      closeCoinsModal();
      fetchShopData({ silent: true });
    } catch (err) {
      toast.error(
        err?.response?.data?.message ||
          `Failed to ${mode === "take" ? "take" : "transfer"} coins.`
      );
    } finally {
      setSubmittingCoins(false);
    }
  };

  const toggleSidebar = () => setSidebarOpen((previous) => !previous);
  const closeSidebar = () => setSidebarOpen(false);

  const quickActions = [
    {
      title: "Manage Computers",
      description: "Create & view PC terminals",
      to: "/computers",
      icon: Monitor,
    },
    {
      title: "Transfer Coins",
      description: "Allocate coins to PC terminals",
      onClick: () => openCoinsModal("transfer"),
      icon: ArrowRightLeft,
    },
    {
      title: "Winning Claims",
      description: "Process pending winner payouts",
      to: "/pending-winners",
      icon: Trophy,
    },
    {
      title: "Reports & Logs",
      description: "Review betting & transaction history",
      to: "/reports",
      icon: BarChart3,
    },
  ];

  return (
    <div className="flex min-h-screen flex-col bg-[#070A12] text-[#F8FAFC]">
      <Navbar toggleSidebar={toggleSidebar} />

      <div className="relative flex flex-1">
        <Sidebar
          isOpen={sidebarOpen}
          closeMobileSidebar={closeSidebar}
        />

        <main className="min-w-0 flex-1 overflow-auto bg-[#070A12]">
          <div className="mx-auto w-full max-w-[1540px] px-3.5 py-4 sm:px-6 sm:py-6 lg:px-8">
            <div className="mb-4 flex flex-col gap-3 sm:mb-6 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <h1 className="text-xl font-bold tracking-tight text-[#F8FAFC] sm:text-3xl">
                  Shop Dashboard
                </h1>

                <p className="mt-0.5 text-xs text-[#94A3B8] sm:text-sm">
                  Welcome back,{" "}
                  <span className="font-semibold text-[#D4AF37]">
                    {user?.full_name || "Shop Owner"}
                  </span>
                  <span className="mx-1 text-[#64748B] sm:mx-2">·</span>
                  <span className="text-xs text-[#94A3B8]">
                    {new Date().toLocaleDateString("en-IN", {
                      weekday: "short",
                      day: "numeric",
                      month: "short",
                    })}
                  </span>
                </p>
              </div>

              <div className="flex flex-wrap items-center gap-2 sm:gap-3">
                <button
                  onClick={() => fetchShopData({ silent: true })}
                  disabled={refreshing}
                  className="inline-flex h-9 items-center gap-1.5 rounded-lg border border-[rgba(148,163,184,0.14)] bg-[#121827] px-3 text-xs font-medium text-[#94A3B8] transition hover:border-[#D4AF37]/50 hover:bg-[#182033] hover:text-[#F8FAFC] disabled:opacity-60 sm:h-10 sm:rounded-xl sm:px-4 sm:text-sm"
                >
                  <RefreshCw
                    size={14}
                    className={
                      refreshing ? "animate-spin text-[#D4AF37]" : ""
                    }
                  />
                  <span>Refresh</span>
                </button>

                <Link
                  to="/pending-winners"
                  className="group inline-flex h-9 items-center gap-1.5 rounded-lg bg-gradient-to-r from-[#D4AF37] to-[#AA7C11] px-3.5 text-xs font-bold text-[#070A12] shadow-md shadow-[#D4AF37]/10 transition hover:opacity-90 sm:h-10 sm:rounded-xl sm:px-5 sm:text-sm"
                >
                  <Trophy size={14} />
                  <span>Pending Claims</span>
                  {pendingWinners > 0 && (
                    <span className="flex h-4 min-w-4 items-center justify-center rounded-full bg-[#EF4444] px-1 text-[9px] font-extrabold text-white animate-pulse sm:h-5 sm:min-w-5 sm:px-1.5 sm:text-[10px]">
                      {pendingWinners}
                    </span>
                  )}
                </Link>
              </div>
            </div>

            {loading ? (
              <div className="flex min-h-[300px] items-center justify-center rounded-2xl border border-[rgba(148,163,184,0.14)] bg-[#121827]">
                <Loader message="Loading shop statistics..." />
              </div>
            ) : (
              <>
                {/* TOP METRICS GRID - 2 COLUMNS ON MOBILE, 4 COLUMNS ON DESKTOP */}
                <div className="mb-4 grid grid-cols-2 gap-3 sm:mb-6 sm:gap-4 lg:grid-cols-4">
                  {/* Shop Wallet */}
                  <div className="relative overflow-hidden rounded-xl border border-[#D4AF37]/40 bg-gradient-to-br from-[#121827] via-[#161F33] to-[#0D111C] p-3.5 shadow-lg shadow-[#D4AF37]/5 sm:rounded-2xl sm:p-5">
                    <div className="flex items-center justify-between">
                      <p className="text-xs font-semibold uppercase tracking-wider text-[#94A3B8]">
                        Shop Wallet
                      </p>
                      <div className="rounded-lg border border-[#D4AF37]/30 bg-[#0D111C] p-1.5 text-[#D4AF37] sm:rounded-xl sm:p-2">
                        <WalletCards size={16} className="sm:h-[18px] sm:w-[18px]" />
                      </div>
                    </div>
                    <div className="mt-2.5 sm:mt-3">
                      <p className="text-lg font-extrabold text-[#D4AF37] sm:text-2xl">
                        🪙 <AnimatedCounter value={wallet} />
                      </p>
                      <p className="mt-1 text-xs text-[#94A3B8]">
                        Available shop balance
                      </p>
                    </div>
                  </div>

                  {/* Transfer Coins */}
                  <button
                    type="button"
                    onClick={() => openCoinsModal("transfer")}
                    className="group w-full text-left rounded-xl border border-[rgba(148,163,184,0.14)] bg-[#121827] p-3.5 transition hover:border-[#22C55E]/50 hover:bg-[#182033] sm:rounded-2xl sm:p-5"
                  >
                    <div className="flex items-center justify-between">
                      <p className="text-xs font-semibold uppercase tracking-wider text-[#94A3B8]">
                        Transfer Coins
                      </p>
                      <div className="rounded-lg border border-[rgba(148,163,184,0.14)] bg-[#0D111C] p-1.5 text-[#22C55E] transition group-hover:scale-105 sm:rounded-xl sm:p-2">
                        <ArrowUpRight size={16} className="sm:h-[18px] sm:w-[18px]" />
                      </div>
                    </div>
                    <div className="mt-2.5 flex items-baseline justify-between sm:mt-3">
                      <p className="text-lg font-extrabold text-[#F8FAFC] sm:text-2xl">
                        🪙 <AnimatedCounter value={transferredCoins} />
                      </p>
                      <span className="inline-flex items-center text-xs text-[#22C55E] group-hover:underline">
                        Transfer →
                      </span>
                    </div>
                    <p className="mt-1 text-xs text-[#64748B]">
                      Coins sent to computers
                    </p>
                  </button>

                  {/* Computers / PC Terminals */}
                  <Link
                    to="/computers"
                    className="group rounded-xl border border-[rgba(148,163,184,0.14)] bg-[#121827] p-3.5 transition hover:border-[#38BDF8]/50 hover:bg-[#182033] sm:rounded-2xl sm:p-5"
                  >
                    <div className="flex items-center justify-between">
                      <p className="text-xs font-semibold uppercase tracking-wider text-[#94A3B8]">
                        Computers
                      </p>
                      <div className="rounded-lg border border-[rgba(148,163,184,0.14)] bg-[#0D111C] p-1.5 text-[#38BDF8] transition group-hover:scale-105 sm:rounded-xl sm:p-2">
                        <Monitor size={16} className="sm:h-[18px] sm:w-[18px]" />
                      </div>
                    </div>
                    <div className="mt-2.5 flex items-baseline justify-between sm:mt-3">
                      <p className="text-lg font-extrabold text-[#F8FAFC] sm:text-2xl">
                        <AnimatedCounter value={totalComputers} />
                      </p>
                      <span className="inline-flex items-center text-xs text-[#38BDF8] group-hover:underline">
                        Manage →
                      </span>
                    </div>
                    <p className="mt-1 text-xs text-[#64748B]">
                      Registered PC terminals
                    </p>
                  </Link>

                  {/* Take Coins */}
                  <button
                    type="button"
                    onClick={() => openCoinsModal("take")}
                    className="group w-full text-left rounded-xl border border-[rgba(148,163,184,0.14)] bg-[#121827] p-3.5 transition hover:border-[#EF4444]/50 hover:bg-[#182033] sm:rounded-2xl sm:p-5"
                  >
                    <div className="flex items-center justify-between">
                      <p className="text-xs font-semibold uppercase tracking-wider text-[#94A3B8]">
                        Take Coins
                      </p>
                      <div className="rounded-lg border border-[rgba(148,163,184,0.14)] bg-[#0D111C] p-1.5 text-[#EF4444] transition group-hover:scale-105 sm:rounded-xl sm:p-2">
                        <ArrowDownLeft size={16} className="sm:h-[18px] sm:w-[18px]" />
                      </div>
                    </div>
                    <div className="mt-2.5 flex items-baseline justify-between sm:mt-3">
                      <p className="text-lg font-extrabold text-[#F8FAFC] sm:text-2xl">
                        🪙 <AnimatedCounter value={takenCoins} />
                      </p>
                      <span className="inline-flex items-center text-xs text-[#EF4444] group-hover:underline">
                        Take →
                      </span>
                    </div>
                    <p className="mt-1 text-xs text-[#64748B]">
                      Coins taken from computers
                    </p>
                  </button>
                </div>

                {/* QUICK OPERATIONS - 1 COLUMN LIST ON MOBILE, 2 COLUMNS ON DESKTOP */}
                <div className="rounded-xl border border-[rgba(148,163,184,0.14)] bg-[#121827] p-4 sm:rounded-2xl sm:p-5">
                  <div className="mb-3 sm:mb-4">
                    <h3 className="text-base font-bold text-[#F8FAFC]">
                      Quick Operations
                    </h3>
                    <p className="text-xs text-[#94A3B8]">
                      Direct shop management tools
                    </p>
                  </div>

                  <div className="grid grid-cols-1 gap-2.5 sm:grid-cols-2 sm:gap-3">
                    {quickActions.map(
                      ({ title, description, to, onClick, icon: Icon }) => {
                        const content = (
                          <div className="flex items-center justify-between w-full">
                            <div className="flex items-center gap-3 sm:gap-3.5">
                              <div className="rounded-lg border border-[#D4AF37]/20 bg-[#121827] p-2 text-[#D4AF37] transition group-hover:bg-[#D4AF37] group-hover:text-[#070A12] sm:rounded-xl sm:p-2.5">
                                <Icon size={18} className="sm:h-[20px] sm:w-[20px]" />
                              </div>

                              <div>
                                <p className="text-sm font-semibold text-[#F8FAFC] group-hover:text-[#D4AF37]">
                                  {title}
                                </p>
                                <p className="text-xs text-[#64748B]">
                                  {description}
                                </p>
                              </div>
                            </div>

                            <ArrowRight
                              size={16}
                              className="text-[#64748B] transition group-hover:translate-x-1 group-hover:text-[#D4AF37]"
                            />
                          </div>
                        );

                        return onClick ? (
                          <button
                            key={title}
                            type="button"
                            onClick={onClick}
                            className="group text-left flex items-center justify-between rounded-lg border border-[rgba(148,163,184,0.12)] bg-[#0D111C] p-3 transition hover:border-[#D4AF37]/50 hover:bg-[#182033] sm:rounded-xl sm:p-4"
                          >
                            {content}
                          </button>
                        ) : (
                          <Link
                            key={title}
                            to={to}
                            className="group flex items-center justify-between rounded-lg border border-[rgba(148,163,184,0.12)] bg-[#0D111C] p-3 transition hover:border-[#D4AF37]/50 hover:bg-[#182033] sm:rounded-xl sm:p-4"
                          >
                            {content}
                          </Link>
                        );
                      }
                    )}
                  </div>
                </div>

                {/* FOOTER */}
                <div className="mt-6 flex items-center justify-between border-t border-[rgba(148,163,184,0.14)] pt-4 text-xs text-[#64748B] sm:mt-8">
                  <p>
                    © {new Date().getFullYear()} Casino Admin Panel · Shop
                    Workspace
                  </p>

                  <div className="flex items-center gap-2 text-[#22C55E]">
                    <span className="h-1.5 w-1.5 rounded-full bg-[#22C55E]" />
                    <span>Live Sync Active</span>
                  </div>
                </div>
              </>
            )}
          </div>
        </main>
      </div>

      {/* DIRECT TRANSFER / TAKE COINS MODAL */}
      {coinsModal && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/75 p-4 backdrop-blur-sm">
          <div className="w-full max-w-md overflow-hidden rounded-2xl border border-[rgba(148,163,184,0.16)] bg-[#121827] shadow-2xl">
            <div className="flex items-center justify-between border-b border-[rgba(148,163,184,0.14)] px-5 py-4">
              <div className="flex items-center gap-3">
                <div
                  className={`flex h-10 w-10 items-center justify-center rounded-xl border ${
                    coinsModal.mode === "take"
                      ? "border-red-500/30 bg-red-500/10 text-red-400"
                      : "border-[#22C55E]/30 bg-[#22C55E]/10 text-[#22C55E]"
                  }`}
                >
                  {coinsModal.mode === "take" ? (
                    <ArrowDownLeft size={20} />
                  ) : (
                    <ArrowUpRight size={20} />
                  )}
                </div>
                <div>
                  <h2 className="text-base font-bold text-[#F8FAFC]">
                    {coinsModal.mode === "take"
                      ? "Take Coins from Computer"
                      : "Transfer Coins to Computer"}
                  </h2>
                  <p className="mt-0.5 text-xs text-[#94A3B8]">
                    {coinsModal.mode === "take"
                      ? "Reclaim coins from PC terminal to shop wallet"
                      : "Allocate coins directly from shop wallet to PC terminal"}
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={closeCoinsModal}
                className="flex h-8 w-8 items-center justify-center rounded-lg text-[#64748B] transition hover:bg-white/5 hover:text-[#F8FAFC]"
              >
                ✕
              </button>
            </div>

            <form onSubmit={handleCoinsSubmit} className="p-5">
              {loadingComputers ? (
                <div className="flex min-h-[140px] flex-col items-center justify-center gap-2">
                  <span className="h-6 w-6 animate-spin rounded-full border-2 border-[#D4AF37]/30 border-t-[#D4AF37]" />
                  <span className="text-xs text-[#94A3B8]">Loading computers...</span>
                </div>
              ) : computersList.length === 0 ? (
                <div className="py-6 text-center">
                  <p className="text-sm font-semibold text-[#F8FAFC]">No Computers Found</p>
                  <p className="mt-1 text-xs text-[#64748B]">
                    Please create a computer first from the Computers page.
                  </p>
                  <Link
                    to="/computers"
                    onClick={closeCoinsModal}
                    className="mt-4 inline-flex items-center gap-2 rounded-xl bg-[#0D111C] px-4 py-2 text-xs font-bold text-[#D4AF37] border border-[#D4AF37]/30 hover:bg-[#182033]"
                  >
                    Go to Computers →
                  </Link>
                </div>
              ) : (
                <div className="space-y-4">
                  <div>
                    <label className="mb-2 block text-xs font-semibold uppercase tracking-wide text-[#94A3B8]">
                      Select Computer PC Terminal
                    </label>
                    <select
                      value={selectedComputerId}
                      onChange={(e) => setSelectedComputerId(e.target.value)}
                      className="w-full rounded-xl border border-[rgba(148,163,184,0.18)] bg-[#0D111C] py-2.5 px-3 text-sm text-[#F8FAFC] outline-none transition focus:border-[#D4AF37]/60"
                    >
                      {computersList.map((comp) => {
                        const compBal = comp.wallet ?? comp.coins ?? comp.current_balance ?? comp.balance ?? comp.credits ?? 0;
                        return (
                          <option key={comp.id} value={comp.id} className="bg-[#121827]">
                            {comp.computer_code} — Wallet: 🪙 {formatCoins(compBal)} ({comp.status})
                          </option>
                        );
                      })}
                    </select>
                  </div>

                  {(() => {
                    const selectedComp = computersList.find(
                      (c) => String(c.id) === String(selectedComputerId)
                    );
                    const selectedCompWallet = selectedComp
                      ? (selectedComp.wallet ?? selectedComp.coins ?? selectedComp.current_balance ?? selectedComp.balance ?? selectedComp.credits ?? 0)
                      : 0;

                    return selectedComp ? (
                      <div className="flex items-center justify-between rounded-xl border border-[rgba(212,175,55,0.25)] bg-[#0D111C] p-3.5">
                        <div>
                          <span className="text-[10px] font-semibold uppercase tracking-wider text-[#94A3B8]">
                            Computer Terminal Info
                          </span>
                          <p className="text-sm font-bold text-[#F8FAFC]">
                            {selectedComp.computer_code}{" "}
                            <span
                              className={`ml-1 text-[10px] px-2 py-0.5 rounded-full font-bold ${
                                selectedComp.status === "ACTIVE"
                                  ? "bg-[#22C55E]/10 text-[#4ADE80] border border-[#22C55E]/20"
                                  : "bg-red-500/10 text-red-400 border border-red-500/20"
                              }`}
                            >
                              {selectedComp.status}
                            </span>
                          </p>
                        </div>
                        <div className="text-right">
                          <span className="text-[10px] font-semibold text-[#94A3B8]">
                            Current PC Wallet:
                          </span>
                          <p className="text-base font-extrabold text-[#D4AF37]">
                            🪙 {formatCoins(selectedCompWallet)}
                          </p>
                        </div>
                      </div>
                    ) : null;
                  })()}

                  <div>
                    <label className="mb-2 block text-xs font-semibold uppercase tracking-wide text-[#94A3B8]">
                      Coin Amount
                    </label>
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-[#D4AF37]">
                        🪙
                      </span>
                      <input
                        type="number"
                        min="1"
                        step="any"
                        value={coinAmount}
                        onChange={(e) => setCoinAmount(e.target.value)}
                        autoFocus
                        placeholder="Enter amount (e.g. 500)"
                        className="w-full rounded-xl border border-[rgba(148,163,184,0.18)] bg-[#0D111C] py-2.5 pl-10 pr-4 text-sm text-[#F8FAFC] outline-none placeholder:text-[#475569] focus:border-[#D4AF37]/60"
                      />
                    </div>
                  </div>

                  <div className="mt-6 flex justify-end gap-3 pt-2">
                    <button
                      type="button"
                      onClick={closeCoinsModal}
                      className="rounded-xl border border-[rgba(148,163,184,0.18)] bg-[#0D111C] px-4 py-2.5 text-xs font-semibold text-[#94A3B8] transition hover:text-[#F8FAFC]"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={submittingCoins}
                      className={`inline-flex items-center gap-2 rounded-xl px-5 py-2.5 text-xs font-bold transition disabled:opacity-50 ${
                        coinsModal.mode === "take"
                          ? "bg-gradient-to-r from-red-500 to-red-600 text-white"
                          : "bg-gradient-to-r from-[#D4AF37] to-[#AA7C11] text-[#070A12]"
                      }`}
                    >
                      {submittingCoins ? (
                        <>
                          <span className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-current border-t-transparent" />
                          Processing...
                        </>
                      ) : coinsModal.mode === "take" ? (
                        "Take Coins"
                      ) : (
                        "Transfer Coins"
                      )}
                    </button>
                  </div>
                </div>
              )}
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ShopDashboard;
